/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject } from "@angular/core";
import { EChartsType } from "echarts";
import { defer, map, Subject } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ChartComponent } from "../../../../shared/components/chart/chart.component";
import { AdvancedFilters } from "../../../../shared/models/http/dashboard/advanced-filters.model";
import { SoftwareAssetsGlobalForm } from "../../../../shared/models/ui/software-assets-global-form.model";
import { AuthorizationService } from "../../../../shared/services/authorization.service";
import { DashboardImpactService } from "../../../../shared/services/dashboard-impact.service";
import { DashboardStateService } from "../../../../shared/services/state/dashboard-state.service";
import { GRANTS } from "../../../../shared/utils/grants.constants";
import { lazyLoad } from "../../../../shared/utils/lazy-load";
import { formatDateForBE, saveAs } from "../../../../shared/utils/utils";

@Component({
	selector: "globalictcc-environmental-impact",
	standalone: true,
	imports: [AsyncPipe, ChartComponent, ButtonComponent],
	templateUrl: "./environmental-impact.component.html",
	styleUrl: "./environmental-impact.component.scss"
})
export class EnvironmentalImpactComponent {
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.swart;

	private readonly _worst10AppsRefresh$ = new Subject<void>();
	private readonly _worst10LayersRefresh$ = new Subject<void>();
	private readonly _filters$ = defer(
		() => this._dashboardStateService.softwareAssetsGlobalForm$
	).pipe(map(form => this._buildAdvancedFilters(form)));

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly worst10Apps$ = lazyLoad(
		this._filters$,
		filters => this._dashboardImpactService.getWorst10Apps(filters),
		this._worst10AppsRefresh$
	);

	public readonly worst10Layers$ = lazyLoad(
		this._filters$,
		filters => this._dashboardImpactService.getWorst10Layers(filters),
		this._worst10LayersRefresh$
	);

	constructor(
		private _dashboardImpactService: DashboardImpactService,
		private _dashboardStateService: DashboardStateService,
		public authorizationService: AuthorizationService
	) {}

	public onWorst10AppsChartRefresh() {
		this._worst10AppsRefresh$.next();
	}

	public onWorst10LayersChartRefresh() {
		this._worst10LayersRefresh$.next();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	private _buildAdvancedFilters(form: SoftwareAssetsGlobalForm): AdvancedFilters | undefined {
		const startDate = formatDateForBE(form.timePeriod?.start);
		const endDate = formatDateForBE(form.timePeriod?.end);
		if (!startDate || !endDate) return undefined;

		const { applicationsSelection, layersSelection } = form;
		return {
			startDate,
			endDate,
			allApplicationsSelected: applicationsSelection.allSelected,
			allLayersSelected: layersSelection.allSelected,
			applicationsSelection: {
				selectedIds: applicationsSelection.selectedIds,
				excludedIds: applicationsSelection.excludedIds
			},
			layersSelection: {
				selectedIds: layersSelection.selectedIds,
				excludedIds: layersSelection.excludedIds
			}
		};
	}
}
